import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FOUNDATION } from "@/data/content";
import { LayoutDashboard, Newspaper, Image, Sparkles, FileText, Users, UserCircle, Inbox, LogOut, ExternalLink, Settings, FileEdit, Palette, HeartHandshake, ShieldCheck } from "lucide-react";
import { Toaster } from "sonner";
import { getImageUrl } from "@/lib/api";

// roles: which roles can see this menu item. If omitted → all authed staff.
const NAV_ITEMS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/pages", label: "Halaman", icon: FileEdit },
  { to: "/admin/news", label: "Berita", icon: Newspaper },
  { to: "/admin/gallery", label: "Galeri", icon: Image },
  { to: "/admin/programs", label: "Program", icon: Sparkles },
  { to: "/admin/publications", label: "Publikasi", icon: FileText },
  { to: "/admin/partners", label: "Mitra", icon: Users },
  { to: "/admin/team", label: "Tim", icon: UserCircle },
  { to: "/admin/donations", label: "Donasi", icon: HeartHandshake, roles: ["super_admin", "admin"] },
  { to: "/admin/submissions", label: "Submissions", icon: Inbox, roles: ["super_admin", "admin"] },
  { to: "/admin/site-settings", label: "Pengaturan Situs", icon: Palette, roles: ["super_admin", "admin"] },
  { to: "/admin/users", label: "Manajemen User", icon: ShieldCheck, roles: ["super_admin"] },
  { to: "/admin/settings", label: "Akun Saya", icon: Settings },
];

const ROLE_LABELS = { super_admin: "Super Admin", admin: "Admin", editor: "Editor" };
const ROLE_COLORS = { super_admin: "bg-red-50 text-red-700", admin: "bg-blue-50 text-blue-700", editor: "bg-emerald-50 text-emerald-700" };

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || "editor";
  const visibleItems = NAV_ITEMS.filter((n) => !n.roles || n.roles.includes(role));

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#F5F6FA] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col shrink-0 sticky top-0 h-screen">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <img src={getImageUrl(FOUNDATION.logo)} alt="Sauyunan" className="h-10 w-10" />
            <div>
              <div className="text-sm font-bold tracking-tight text-[var(--brand-blue)]">SAUYUNAN</div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--brand-orange)]">CMS</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {visibleItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl mb-1 text-sm font-medium transition ${
                  isActive ? "bg-[var(--brand-blue)] text-white" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-200 space-y-2">
          <div className="px-3 py-2 rounded-lg bg-slate-50">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Logged in as</div>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${ROLE_COLORS[role] || ROLE_COLORS.editor}`}>{ROLE_LABELS[role] || role}</span>
            </div>
            <div className="text-sm font-semibold text-slate-800 truncate">{user?.email}</div>
          </div>
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-slate-600 hover:text-[var(--brand-blue)] px-3 py-1.5">
            <ExternalLink className="h-3.5 w-3.5" /> Buka Situs Publik
          </a>
          <button onClick={handleLogout} data-testid="admin-logout" className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition">
            <LogOut className="h-4 w-4" /> Keluar
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="p-6 md:p-10">
          <Outlet />
        </div>
        <Toaster position="top-right" richColors closeButton />
      </main>
    </div>
  );
}
